import React, { useCallback, useEffect, useState } from 'react';
import {
  Drawer,
  Descriptions,
  Table,
  Typography,
  Spin,
  Alert,
  Tag,
} from 'antd';
import { getCurrency, getCurrencyExchangeRates } from '../services/api';
import { alexforbesBrand } from '../brand/alexforbes';
import { formatDisplayDate } from '../utils/dates';

const { Text, Title } = Typography;

const formatDate = (value) => formatDisplayDate(value);

const formatRate = (value) => {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 8,
  });
};

const CurrencyDetailDrawer = ({ currencyId, open, onClose, initialCurrency }) => {
  const [currency, setCurrency] = useState(initialCurrency || null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const [rates, setRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState(null);
  const [ratePagination, setRatePagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  useEffect(() => {
    if (!open || !currencyId) return;
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setCurrency(initialCurrency || null);

    (async () => {
      try {
        const data = await getCurrency(currencyId);
        if (!cancelled) setCurrency(data);
      } catch {
        if (!cancelled) setDetailError('Could not load currency details.');
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, currencyId, initialCurrency]);

  const loadRates = useCallback(
    async (page, pageSize) => {
      const code = currency?.code || initialCurrency?.code;
      if (!code) return;
      const cappedSize = Math.min(Math.max(pageSize, 1), 100);
      setRatesLoading(true);
      setRatesError(null);
      try {
        const data = await getCurrencyExchangeRates(code, page, cappedSize);
        setRates(data.results || []);
        setRatePagination({
          current: page,
          pageSize: cappedSize,
          total: data.count ?? 0,
        });
      } catch {
        setRatesError('Could not load FX rate history.');
        setRates([]);
      } finally {
        setRatesLoading(false);
      }
    },
    [currency?.code, initialCurrency?.code]
  );

  useEffect(() => {
    const code = currency?.code || initialCurrency?.code;
    if (!open || !code) return;
    loadRates(1, ratePagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when drawer opens
  }, [open, currency?.code, initialCurrency?.code, loadRates]);

  const handleRateTableChange = (pag) => {
    loadRates(pag.current, pag.pageSize);
  };

  const focusCode = currency?.code;

  const rateColumns = [
    {
      title: 'Effective date',
      dataIndex: 'effective_date',
      key: 'effective_date',
      render: (v) => formatDate(v),
    },
    {
      title: 'Pair',
      key: 'pair',
      render: (_, row) => (
        <Text strong>
          {row.base_currency?.code}/{row.quote_currency?.code}
        </Text>
      ),
    },
    {
      title: 'Role',
      key: 'role',
      width: 100,
      render: (_, row) => {
        if (row.base_currency?.code === focusCode) {
          return <Tag color={alexforbesBrand.colors.teal}>Base</Tag>;
        }
        if (row.quote_currency?.code === focusCode) {
          return <Tag color={alexforbesBrand.colors.orange}>Quote</Tag>;
        }
        return '—';
      },
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
      key: 'rate',
      align: 'right',
      render: (v, row) => (
        <span>
          <Text type="secondary" style={{ fontSize: 11 }}>
            1 {row.base_currency?.code} ={' '}
          </Text>
          <Text strong style={{ color: alexforbesBrand.colors.teal }}>
            {formatRate(v)} {row.quote_currency?.code}
          </Text>
        </span>
      ),
    },
    {
      title: 'Recorded',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v) => formatDate(v),
    },
  ];

  return (
    <Drawer
      title={currency ? `${currency.code} — ${currency.name}` : 'Currency details'}
      width={720}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {detailLoading && !currency ? (
        <Spin />
      ) : (
        <>
          {detailError && (
            <Alert type="warning" showIcon message={detailError} style={{ marginBottom: 16 }} />
          )}
          {currency && (
            <>
              <Descriptions bordered size="small" column={1} style={{ marginBottom: 24 }}>
                <Descriptions.Item label="Code">
                  <Text strong>{currency.code}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Name">{currency.name}</Descriptions.Item>
                <Descriptions.Item label="Symbol">{currency.symbol || '—'}</Descriptions.Item>
                <Descriptions.Item label="Latest FX date">
                  {formatDate(currency.latest_rate_date)}
                </Descriptions.Item>
                <Descriptions.Item label="FX records">
                  {currency.fx_rate_count != null ? currency.fx_rate_count : '—'}
                </Descriptions.Item>
              </Descriptions>

              <Title level={5} style={{ marginBottom: 12 }}>
                FX rate history
              </Title>
              <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
                Rates where {currency.code} is base or quote (1 base = rate × quote).
              </Text>
              {ratesError && (
                <Alert type="error" showIcon message={ratesError} style={{ marginBottom: 12 }} />
              )}
              <Table
                rowKey="id"
                size="small"
                columns={rateColumns}
                dataSource={rates}
                loading={ratesLoading}
                pagination={{
                  current: ratePagination.current,
                  pageSize: ratePagination.pageSize,
                  total: ratePagination.total,
                  showSizeChanger: true,
                  pageSizeOptions: ['20', '50', '100'],
                  showTotal: (total) => `${total} FX records`,
                }}
                onChange={handleRateTableChange}
                locale={{ emptyText: 'No FX rates recorded for this currency yet' }}
              />
            </>
          )}
        </>
      )}
    </Drawer>
  );
};

export default CurrencyDetailDrawer;
